/**
 * QIPSubmitModal — one-click "Submit to QIP" dialog.
 *
 * Lets Nash type the recipient email + an optional message and posts to
 * /api/qip-submit, which generates the evidence pack ZIP, scorecard PDF, and
 * master-checklist CSV server-side and sends them via Resend.
 *
 * The recipient is always entered manually (not hard-coded) so the same
 * button can be used for QIP, RANZCR, internal exec, etc.
 */
import { useState } from 'react';
import axios from 'axios';
import { Send, Mail, Loader2, X, CheckCircle2, AlertTriangle, Paperclip } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = '#42CDD7';

export default function QIPSubmitModal({ onClose }) {
  const [toEmail, setToEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState(
    'Please find attached the current DIAS readiness evidence for our practice. ' +
    'Happy to provide any additional artefacts on request.'
  );
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const send = async () => {
    if (!toEmail || !/^\S+@\S+\.\S+$/.test(toEmail)) {
      setResult({ type: 'error', text: 'Please enter a valid recipient email.' });
      return;
    }
    setBusy(true); setResult(null);
    try {
      const r = await axios.post(`${API}/qip-submit`,
        { to_email: toEmail, subject, message },
        { withCredentials: true });
      const sizes = (r.data.attachments_summary || []).reduce((s, a) => s + a.size, 0);
      setResult({
        type: 'success',
        text: `Email sent to ${r.data.to_email}. ${r.data.attachments_summary.length} attachments (${(sizes / 1024).toFixed(0)} KB total). Resend ID: ${r.data.message_id?.slice(0, 8) || '—'}.`,
      });
    } catch (e) {
      setResult({ type: 'error', text: e.response?.data?.detail || 'Send failed.' });
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="qip-submit-modal">
      <div className="absolute inset-0 bg-black/50" onClick={busy ? undefined : onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="inline-flex items-center gap-2 px-2 py-0.5 rounded-full mb-1.5"
              style={{ backgroundColor: `${C}1A`, color: C }}>
              <Send className="w-3 h-3" />
              <span className="text-[9px] font-bold uppercase tracking-wider">Submit to QIP</span>
            </div>
            <h2 className="font-['Outfit'] text-xl font-bold text-foreground">Email evidence pack</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Sends the latest <strong>evidence pack ZIP</strong>, <strong>scorecard PDF</strong>, and <strong>master-checklist CSV</strong> as attachments. Recipient is entered manually each send.
            </p>
          </div>
          <button onClick={onClose} disabled={busy} data-testid="qip-modal-close"
            className="text-muted-foreground hover:text-foreground p-1">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3 mb-4">
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">To email *</span>
            <input type="email" value={toEmail} onChange={e => setToEmail(e.target.value)}
              data-testid="qip-to-email" placeholder="e.g. assessor@qip.com.au" autoFocus
              className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
          </label>

          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Subject (optional — auto-generated if blank)</span>
            <input type="text" value={subject} onChange={e => setSubject(e.target.value)}
              data-testid="qip-subject"
              placeholder="Auto: i3 Radiology — DIAS / QIP submission · 2026-05-05"
              className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
          </label>

          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Message (optional intro paragraph)</span>
            <textarea value={message} onChange={e => setMessage(e.target.value)}
              data-testid="qip-message" rows={4}
              className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2 resize-y" />
          </label>
        </div>

        <div className="text-[11px] text-muted-foreground mb-4 inline-flex items-center gap-1.5">
          <Paperclip className="w-3 h-3" />
          Attachments are generated fresh at send-time from the current database state.
        </div>

        {result && (
          <div data-testid="qip-result" className={`rounded-lg border p-3 text-xs mb-3 flex items-start gap-2 ${result.type === 'success'
            ? 'bg-green-500/5 border-green-500/30 text-green-600'
            : 'bg-red-500/5 border-red-500/30 text-red-600'}`}>
            {result.type === 'success' ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}
            <span>{result.text}</span>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <button onClick={onClose} disabled={busy} data-testid="qip-cancel"
            className="text-sm font-semibold px-3 py-1.5 rounded-lg border border-border">
            {result?.type === 'success' ? 'Done' : 'Cancel'}
          </button>
          {result?.type !== 'success' && (
            <button onClick={send} disabled={busy || !toEmail} data-testid="qip-send-btn"
              className="text-sm font-semibold px-4 py-1.5 rounded-lg text-white inline-flex items-center gap-1.5 disabled:opacity-50"
              style={{ backgroundColor: C }}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
              {busy ? 'Sending…' : 'Send to QIP'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
